import { isRef, type Ref } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Composable for handling reactive translations based on model configuration.
 *
 * Labels are resolved from translation files using keys:
 * - Fields: models.{prefix}.fields.{key}
 * - Tabs: models.{prefix}.tabs.{key}
 * - Blocks: models.{prefix}.blocks.{key}
 * - Actions: models.{prefix}.actions.{key}
 * - Filters: models.{prefix}.filters.{key}
 * - Badges: models.{prefix}.badges.{key}
 */
export function useTranslatable(
    config?:
        | {
            translationPrefix?: string | null;
        }
        | Ref<
            | {
                translationPrefix?: string | null;
            }
            | null
            | undefined
        >
) {
    const { t, te } = useI18n();

    const getTranslationPrefix = () => {
        if (!config) return null;
        if (isRef(config)) {
            return config.value?.translationPrefix;
        }
        return config.translationPrefix;
    };

    /**
     * Translate a key using the model's translation prefix.
     * Returns a human-readable fallback if no translation found.
     */
    const tr = (key: string, prefixOverride?: string | null): string => {
        const prefix = prefixOverride || getTranslationPrefix();

        if (prefix && key) {
            const cleanPrefix = prefix.replace("models.", "");
            const fullKey = `models.${cleanPrefix}.${key}`;
            if (te(fullKey)) {
                return t(fullKey);
            }
        }

        // Global fallbacks for actions
        if (key.startsWith("actions.")) {
            const actionName = key.replace("actions.", "");
            if (te(`crud.actions.${actionName}`)) {
                return t(`crud.actions.${actionName}`);
            }
            if (te(`crud.${actionName}`)) {
                return t(`crud.${actionName}`);
            }
        }

        // Fallback to a readable version of the key
        return key.split(".").pop() || key;
    };

    /**
     * Translate a field label.
     * key: 'fields.field_name'
     *
     * For dot-notation fields (e.g. 'employeeProfile.app_data.punch_time'),
     * tries the full path first, then progressively strips leading segments
     * until a translation is found.
     */
    const trField = (fieldName: string): string => {
        const segments = fieldName.split('.');
        // Try full key, then progressively strip leading segments
        for (let i = 0; i < segments.length; i++) {
            const candidate = segments.slice(i).join('.');
            const result = tr(`fields.${candidate}`);
            // tr() returns the last segment as fallback — if we got something different, it resolved
            if (result !== segments[segments.length - 1]) return result;
        }
        return tr(`fields.${fieldName}`);
    };

    /**
     * Translate a tab label.
     * key: 'tabs.tab_key'
     */
    const trTab = (tabKey: string): string => {
        return tr(`tabs.${tabKey}`);
    };

    /**
     * Translate an action label.
     * key: 'actions.action_name'
     */
    const trAction = (actionName: string): string => {
        return tr(`actions.${actionName}`);
    };

    /**
     * Translate a badge label.
     * key: 'badges.badge_value'
     */
    const trBadge = (
        badgeValue: string | number | null | undefined,
        defaultLabel?: string
    ): string => {
        if (badgeValue === null || badgeValue === undefined || badgeValue === "") {
            return defaultLabel || String(badgeValue);
        }
        const key = `badges.${badgeValue}`;
        const prefix = getTranslationPrefix();
        if (prefix) {
            const fullKey = `models.${prefix.replace("models.", "")}.${key}`;
            if (te(fullKey)) {
                return t(fullKey);
            }
        }
        return defaultLabel || String(badgeValue);
    };

    /**
     * Translate a field value using field-specific options first, then generic badges.
     *
     * This is useful when a value is rendered outside a FieldConfig context
     * (for example, viewer subtitles or lightweight header displays) but should
     * still resolve to the same localized label users see in selects/badges.
     */
    const trValue = (
        fieldName: string,
        fieldValue: string | number | boolean | null | undefined,
        defaultLabel?: string
    ): string => {
        if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
            return defaultLabel || String(fieldValue);
        }

        const strValue = String(fieldValue);
        const prefix = getTranslationPrefix();

        if (prefix) {
            const cleanPrefix = prefix.replace("models.", "");
            const segments = fieldName.split('.');

            for (let i = 0; i < segments.length; i++) {
                const candidate = segments.slice(i).join('.');
                const optionsKey = `models.${cleanPrefix}.options.${candidate}.${strValue}`;
                if (te(optionsKey)) {
                    return t(optionsKey);
                }
            }

            const badgeKey = `models.${cleanPrefix}.badges.${strValue}`;
            if (te(badgeKey)) {
                return t(badgeKey);
            }
        }

        if (fieldValue === true || strValue === 'true') {
            return t('crud.yes');
        }

        if (fieldValue === false || strValue === 'false') {
            return t('crud.no');
        }

        return defaultLabel || strValue;
    };

    /**
     * Translate a block label.
     * key: 'blocks.block_key'
     */
    const trBlock = (blockKey: string): string => {
        return tr(`blocks.${blockKey}`);
    };

    /**
     * Translate a filter label.
     * key: 'filters.filter_key'
     */
    const trFilter = (filterKey: string): string => {
        return tr(`filters.${filterKey}`);
    };

    return {
        tr,
        trField,
        trTab,
        trAction,
        trBadge,
        trValue,
        trBlock,
        trFilter,
        t,
        te
    };
}
